import React, { useContext, useEffect } from "react";

import archive from "@/assets/svg/menu-icons/arkiv.svg?react";
import materialhanding from "@/assets/svg/menu-icons/crane.svg?react";
import info from "@/assets/svg/menu-icons/info.svg?react";
import competency from "@/assets/svg/menu-icons/kompetanse.svg?react";
import lab from "@/assets/svg/menu-icons/lab.svg?react";
import tasks from "@/assets/svg/menu-icons/oppgave.svg?react";
import procedures from "@/assets/svg/menu-icons/prosedyrer.svg?react";
import shifts from "@/assets/svg/menu-icons/skift.svg?react";

import { MenuItem } from "@/model/Application/MenuItem";
import { NavElement } from "@/model/Nav/NavElement";
import { UserContext } from "@/model/User/UserContext";

import {
	checkGroupAccess,
	useFetchActiveUserCompanyConfiguration,
} from "@/service/ApplicationQueries";
import { useFetchOrganizationModules } from "@/service/Settings/ModulesQueries";
import { useFetchBrandingSettings } from "@/service/Settings/SettingsQueries";

// This file is not a query file but more like a sample of custom hooks - Ustin

export const useFetchAllLeftSidebarMenuItems = () => {
	const { isLoading, data: defaultMenu } = useFetchDefaultLeftSidebarItems();
	const { isLoading: isCustomLoading, data: customMenu } =
		useFetchCustomLeftSidebarItems();

	if (isLoading && isCustomLoading) {
		return new Array<NavElement>();
	}

	return [...defaultMenu, ...customMenu];
};

const useFetchDefaultLeftSidebarItems = () => {
	const {
		data: modules,
		isLoading,
		isFetching,
		isError,
		error,
	} = useFetchOrganizationModules();

	const menuItems = !isLoading
		? getNavElements().filter((elm) => {
				switch (elm.Id) {
					case "hide_competance":
						return modules?.Competence;
					case "hide_lab":
						return modules?.Lab;
					case "hide_materialhanding":
						return modules?.MaterialHandling;
					case "hide_proc":
						return modules?.Procedures;
					case "hide_archive":
						return modules?.Procedures;
					case "hide_shift":
						return modules?.Shift;
					case "hide_task":
						return modules?.Task;
				}

				return false;
			})
		: Array<NavElement>();

	return {
		isLoading,
		isFetching,
		isError,
		error,
		data: menuItems,
	};
};


const useFetchCustomLeftSidebarItems = () => {
	const { data: settings, isLoading } = useFetchBrandingSettings();
	const user = useContext(UserContext);

	if (!user || isLoading || !settings) {
		return { data: [], isLoading: true };
	}

	const isUserAdmin = user.PermissionLevel.Level >= 4;
	const hasUserGroupAccess = checkGroupAccess(user.Groups, [
		settings.AccessGroupId,
	]);

	const isVisibleToUser =
		settings.EnableCustomLink ||
		(settings.IsViewableByAdministratorsOnly && !isUserAdmin) ||
		hasUserGroupAccess;

	const customLinks = isVisibleToUser
		? [
				{
					Id: "custumlink1",
					AcceptedGroups: [settings.AccessGroupId],
					ClassName: "svg-icon svg-icon--info",
					HasGroupAccessControl: settings.AccessGroupId > 0,
					SvgIcon: info,
					Title: settings.LinkText,
					TranslationKey: "",
					Url: settings.Url,
					IsAdminProtected: settings.IsViewableByAdministratorsOnly,
				},
			]
		: [];

	return { data: customLinks, isLoading: false };
};

export const getNavElements = (): Array<NavElement> => {
	return [
		new NavElement({
			Id: "hide_proc",
			ClassName: "svg-icon svg-icon--procedures",
			Title: "Procedures",
			TranslationKey: "Frontpage.Nav.Procedures",
			Url: "tremeny0",
			SvgIcon: procedures,
		}),
		new NavElement({
			Id: "hide_archive",
			ClassName: "svg-icon svg-icon--archive",
			Title: "Archive",
			TranslationKey: "Frontpage.Nav.Archive",
			Url: "archive",
			SvgIcon: archive,
		}),
		new NavElement({
			Id: "hide_competance",
			ClassName: "svg-icon svg-icon--competence",
			Title: "Competency",
			TranslationKey: "Frontpage.Nav.Competency",
			Url: "competence",
			SvgIcon: competency,
		}),
		new NavElement({
			Id: "hide_shift",
			ClassName: "svg-icon svg-icon--shift",
			Title: "Shifts",
			TranslationKey: "Frontpage.Nav.Shifts",
			Url: "shiftLog",
			SvgIcon: shifts,
		}),
		new NavElement({
			Id: "hide_task",
			ClassName: "svg-icon svg-icon--task",
			Title: "Tasks",
			TranslationKey: "Frontpage.Nav.Tasks",
			Url: "taskmain",
			SvgIcon: tasks,
		}),
		new NavElement({
			Id: "hide_lab",
			ClassName: "svg-icon svg-icon--lab",
			Title: "Lab",
			TranslationKey: "Frontpage.Nav.Lab",
			Url: "labinput",
			SvgIcon: lab,
		}),
		new NavElement({
			Id: "hide_materialhanding",
			ClassName: "svg-icon svg-icon--MaterialHandling",
			Title: "Lift",
			TranslationKey: "Frontpage.Nav.MaterialHandling",
			Url: "MaterialHandlingMyPage",
			SvgIcon: materialhanding,
		}),
	];
};

export const useFetchAdminMenuContent = () => {
	const [menuList, setMenuList] = React.useState<Array<MenuItem>>([]);
	const { isLoading, data: config } =
		useFetchActiveUserCompanyConfiguration(true);

	useEffect(() => {
		if (config?.Menu) {
			setMenuList([...config?.Menu]);
		}
	}, [config?.Menu]);

	const toggleMenuExpansion = (menuItem: MenuItem) => {
		const updatedMenuItems = [
			...menuList.map((item) => {
				if (menuItem.Id === item.Id) {
					item.Expanded = !item.Expanded;
				}
				return item;
			}),
		];

		setMenuList(updatedMenuItems);
	};
	return {
		isLoading,
		menuList,
		toggleMenuExpansion,
	};
};
